import type { ReactNode } from 'react';
import { Button, Dialog, Icon, cn } from '@forecastify/ui';
import { useT } from '../i18n';
import { BTN, TONE } from './tones';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The one dialog the redesign draws, for every verb that asks first.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE SHAPE FOR SUSPEND, ACTIVATE, RUN-NOW, INVITE AND REMOVE — THE MOCKUP'S.** A title,
 * a sentence saying what happens, an optional *tier* pill naming the kind of consequence (*Kizárja a
 * tagokat*, *Visszafordíthatatlan*), the fields, and a footer whose left side says what is still
 * missing while the button waits. Five hand-made dialogs had five slightly different footers; the
 * redesign has one, and so does this.
 *
 * ⚠️ **The confirm button's colour is the consequence's**, not the dialog's: amber-filled for
 * something that disrupts and can be undone (suspend, run on live data), red for the irreversible,
 * the plain primary for the rest. `warn` is the mockup's `bg-[var(--warn-fg)] text-background`.
 */

export type ConfirmTone = 'default' | 'warn' | 'destructive' | 'outline';

const CONFIRM: Readonly<Record<ConfirmTone, string>> = {
  default: '',
  warn: 'bg-[var(--status-warn-text)] text-background hover:bg-[var(--status-warn-text)] hover:opacity-90',
  destructive: '',
  outline: '',
};

export function ActionDialog({
  open,
  onClose,
  title,
  description,
  tier,
  blockedText,
  confirm,
  cancelLabel,
  children,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly description?: string;
  readonly tier?: { readonly label: string; readonly tone: 'warn' | 'bad' };
  /** Left of the buttons: what the confirm button is still waiting for. */
  readonly blockedText?: string;
  readonly confirm: {
    readonly label: string;
    readonly onClick: () => void;
    readonly disabled?: boolean;
    readonly tone?: ConfirmTone;
  };
  readonly cancelLabel: string;
  readonly children?: ReactNode;
}): ReactNode {
  const t = useT();
  const tone = confirm.tone ?? 'default';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={title}
      {...(description === undefined ? {} : { description })}
      width="480px"
      /* ⚠️ The corner ✕ is *Bezárás*, not the Cancel button's word. Labelled alike, a screen reader
         announced two "Mégse" buttons in every dialog — and a test could not tell them apart. */
      closeLabel={t('dialog.close')}
    >
      <div className="flex flex-col gap-4">
        {tier === undefined ? null : (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 self-start rounded-full px-2 py-0.5 text-[11px] font-medium tracking-[.03em] uppercase',
              tier.tone === 'warn' ? TONE.warn : TONE.badSoft,
            )}
          >
            <Icon name="triangle-alert" size={11} />
            {tier.label}
          </span>
        )}
        {children}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
          <span className="text-muted-foreground mr-auto text-[12px]">{blockedText ?? ''}</span>
          <Button variant="ghost" className={BTN} onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button
            variant={
              tone === 'destructive' ? 'destructive' : tone === 'outline' ? 'outline' : 'default'
            }
            disabled={confirm.disabled === true}
            onClick={confirm.onClick}
            className={cn(BTN, 'disabled:opacity-45', CONFIRM[tone])}
          >
            {confirm.label}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** A labelled field in the mockup's layout: label above, 12.5px medium. */
export function DialogField({
  label,
  htmlFor,
  error,
  children,
}: {
  readonly label: string;
  readonly htmlFor: string;
  readonly error?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[12.5px] font-medium">
        {label}
      </label>
      {children}
      {error === undefined || error === '' ? null : (
        <span className="text-destructive text-[12px]">{error}</span>
      )}
    </div>
  );
}
